import { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import {
  RotateCcw, RotateCw, FlipHorizontal, Crop as CropIcon, Sliders, Sun, Contrast,
  RefreshCw, Loader2, Check, ZoomIn, ZoomOut, Search,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";

const ASPECTS = [
  { id: "free", label: "Free", value: null, ratio: "auto" },
  { id: "1-1", label: "Square", sub: "1:1", value: 1 },
  { id: "4-3", label: "Landscape", sub: "4:3", value: 4 / 3 },
  { id: "3-4", label: "Portrait", sub: "3:4", value: 3 / 4 },
  { id: "16-9", label: "Wide", sub: "16:9", value: 16 / 9 },
];

async function renderEditedBlob(imageSrc, { rotation, flipH, brightness, contrast, pixelCrop }) {
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.crossOrigin = "anonymous";
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = imageSrc;
  });

  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const boxW = img.width * cos + img.height * sin;
  const boxH = img.width * sin + img.height * cos;

  const stage = document.createElement("canvas");
  stage.width = boxW;
  stage.height = boxH;
  const sctx = stage.getContext("2d");
  sctx.fillStyle = "#ffffff";
  sctx.fillRect(0, 0, boxW, boxH);
  sctx.filter = `brightness(${brightness}%) contrast(${contrast}%)`;
  sctx.translate(boxW / 2, boxH / 2);
  sctx.rotate(rad);
  sctx.scale(flipH ? -1 : 1, 1);
  sctx.drawImage(img, -img.width / 2, -img.height / 2);

  const out = document.createElement("canvas");
  out.width = Math.round(pixelCrop.width);
  out.height = Math.round(pixelCrop.height);
  const octx = out.getContext("2d");
  octx.drawImage(
    stage,
    Math.round(pixelCrop.x), Math.round(pixelCrop.y),
    Math.round(pixelCrop.width), Math.round(pixelCrop.height),
    0, 0,
    Math.round(pixelCrop.width), Math.round(pixelCrop.height),
  );

  return new Promise((resolve) => out.toBlob((b) => resolve(b), "image/jpeg", 0.9));
}

const TABS = [
  { id: "crop", label: "Crop", icon: CropIcon },
  { id: "adjust", label: "Adjust", icon: Sliders },
  { id: "rotate", label: "Rotate", icon: RotateCw },
];

export default function ImageEditorModal({ open, imageUrl, onCancel, onSave }) {
  const [tab, setTab] = useState("crop");
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [aspect, setAspect] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixelCrop, setPixelCrop] = useState(null);
  const [saving, setSaving] = useState(false);
  const [compactViewport, setCompactViewport] = useState(null);

  useEffect(() => {
    if (open) {
      setTab("crop");
      setRotation(0);
      setFlipH(false);
      setBrightness(100);
      setContrast(100);
      setAspect(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setPixelCrop(null);
      setSaving(false);
    }
  }, [open, imageUrl]);

  useEffect(() => {
    if (!open) return undefined;
    const visualViewport = window.visualViewport;
    const syncViewport = () => {
      if (!window.matchMedia("(max-width: 639px)").matches) {
        setCompactViewport(null);
        return;
      }
      setCompactViewport({
        height: Math.floor(visualViewport?.height || window.innerHeight),
        top: Math.floor(visualViewport?.offsetTop || 0),
        left: Math.floor(visualViewport?.offsetLeft || 0),
      });
    };
    syncViewport();
    visualViewport?.addEventListener("resize", syncViewport);
    visualViewport?.addEventListener("scroll", syncViewport);
    window.addEventListener("resize", syncViewport);
    window.addEventListener("orientationchange", syncViewport);
    return () => {
      visualViewport?.removeEventListener("resize", syncViewport);
      visualViewport?.removeEventListener("scroll", syncViewport);
      window.removeEventListener("resize", syncViewport);
      window.removeEventListener("orientationchange", syncViewport);
    };
  }, [open]);

  const onCropComplete = useCallback((_area, areaPixels) => setPixelCrop(areaPixels), []);

  const reset = () => {
    setRotation(0);
    setFlipH(false);
    setBrightness(100);
    setContrast(100);
    setAspect(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const save = async () => {
    setSaving(true);
    try {
      let pc = pixelCrop;
      if (!pc) {
        const img = await new Promise((r) => { const e = new Image(); e.onload = () => r(e); e.src = imageUrl; });
        const rad = (rotation * Math.PI) / 180;
        const sin = Math.abs(Math.sin(rad));
        const cos = Math.abs(Math.cos(rad));
        pc = { x: 0, y: 0, width: img.width * cos + img.height * sin, height: img.width * sin + img.height * cos };
      }
      const blob = await renderEditedBlob(imageUrl, { rotation, flipH, brightness, contrast, pixelCrop: pc });
      if (blob) await onSave(blob);
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    rotation !== 0 || flipH || brightness !== 100 || contrast !== 100 ||
    aspect !== null || zoom !== 1;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel?.()}>
      <DialogContent
        className="!flex h-[100dvh] max-h-[100dvh] w-screen max-w-none flex-col !gap-0 overflow-hidden rounded-none border-0 !p-0 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:w-[96vw] sm:max-w-4xl sm:rounded-2xl sm:border"
        data-testid="image-editor-modal"
        style={compactViewport ? {
          height: `${compactViewport.height}px`,
          maxHeight: `${compactViewport.height}px`,
          top: `${compactViewport.top}px`,
          left: `${compactViewport.left}px`,
          transform: "none",
        } : undefined}
      >
        <DialogTitle className="sr-only">Edit image</DialogTitle>
        <DialogDescription className="sr-only">
          Rotate, flip, crop, or adjust brightness and contrast before uploading.
        </DialogDescription>

        {/* HEADER */}
        <div className="flex shrink-0 items-center justify-between border-b border-zinc-100 px-5 py-3 pr-12">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#E0EDFA] flex items-center justify-center">
              <CropIcon className="w-4 h-4 text-[#0654BA]" />
            </div>
            <div>
              <div className="font-semibold text-sm">Edit photo</div>
              <div className="text-xs text-zinc-500">Drag to move · scroll to zoom</div>
            </div>
          </div>
          {/* Close X is provided by shadcn DialogContent in the top-right */}
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-white"
          data-testid="image-editor-scroll-area"
        >
        {/* IMAGE STAGE */}
        <div className="relative h-[min(43dvh,300px)] min-h-[220px] bg-[#0B0B0B] sm:h-[min(55dvh,520px)] sm:min-h-[320px]">
          {imageUrl && (
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect || undefined}
              minZoom={1}
              maxZoom={4}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition
              showGrid
              objectFit="contain"
              style={{
                mediaStyle: {
                  filter: `brightness(${brightness}%) contrast(${contrast}%)`,
                  transform: flipH ? "scaleX(-1)" : undefined,
                },
                cropAreaStyle: {
                  border: "2px solid #ffffff",
                  boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
                },
              }}
            />
          )}

          {/* ZOOM overlay control — always visible, easy access */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/70 backdrop-blur px-3 py-2 rounded-full">
            <button
              type="button"
              onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))}
              className="text-white/90 hover:text-white p-1"
              data-testid="zoom-out-btn"
              aria-label="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <div className="w-28 sm:w-40">
              <Slider
                min={1}
                max={4}
                step={0.01}
                value={[zoom]}
                onValueChange={(v) => setZoom(v[0])}
                data-testid="zoom-slider"
              />
            </div>
            <button
              type="button"
              onClick={() => setZoom((z) => Math.min(4, +(z + 0.25).toFixed(2)))}
              className="text-white/90 hover:text-white p-1"
              data-testid="zoom-in-btn"
              aria-label="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* TABS */}
        <div className="flex border-b border-zinc-100 bg-white">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition relative ${
                  active ? "text-[#0654BA]" : "text-zinc-500 hover:text-zinc-800"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
                {active && <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-[#0654BA] rounded-full" />}
              </button>
            );
          })}
        </div>

        {/* TAB PANELS */}
        <div className="p-5 bg-white min-h-[136px]">
          {tab === "crop" && (
            <div>
              <div className="text-xs font-medium text-zinc-500 mb-3">Choose a shape</div>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                {ASPECTS.map((a) => {
                  const active = aspect === a.value;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setAspect(a.value)}
                      data-testid={`aspect-${a.id}-btn`}
                      className={`flex flex-col items-center gap-1.5 rounded-xl border-2 p-2.5 transition sm:p-3 ${
                        active
                          ? "border-[#0654BA] bg-[#E0EDFA]"
                          : "border-zinc-200 hover:border-zinc-300 bg-white"
                      }`}
                    >
                      <AspectIcon aspect={a} active={active} />
                      <div className="text-[11px] font-semibold leading-none">{a.label}</div>
                      {a.sub && <div className="text-[10px] text-zinc-500 leading-none">{a.sub}</div>}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-zinc-500 mt-3">
                Drag the photo to reposition inside the crop box. Use the zoom bar to fill the frame.
              </p>
            </div>
          )}

          {tab === "adjust" && (
            <div className="space-y-5">
              <SliderRow
                testid="brightness-slider"
                icon={Sun}
                label="Brightness"
                value={brightness}
                onChange={setBrightness}
                onReset={() => setBrightness(100)}
              />
              <SliderRow
                testid="contrast-slider"
                icon={Contrast}
                label="Contrast"
                value={contrast}
                onChange={setContrast}
                onReset={() => setContrast(100)}
              />
            </div>
          )}

          {tab === "rotate" && (
            <div className="flex flex-wrap gap-3">
              <BigBtn testid="rotate-left-btn" onClick={() => setRotation((r) => r - 90)} icon={RotateCcw} label="Rotate left" sub="90°" />
              <BigBtn testid="rotate-right-btn" onClick={() => setRotation((r) => r + 90)} icon={RotateCw} label="Rotate right" sub="90°" />
              <BigBtn testid="flip-btn" onClick={() => setFlipH((f) => !f)} icon={FlipHorizontal} label="Flip" sub="Mirror" active={flipH} />
            </div>
          )}
        </div>
        </div>

        {/* FOOTER */}
        <div
          className="flex shrink-0 items-center justify-between gap-2 border-t border-zinc-100 bg-zinc-50 px-3 py-3 sm:px-5"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          data-testid="image-editor-footer"
        >
          <button
            type="button"
            onClick={reset}
            disabled={saving || !dirty}
            data-testid="reset-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-600 hover:text-[#0654BA] disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Reset all
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              data-testid="editor-cancel-btn"
              className="rounded-full px-3 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-200 disabled:opacity-50 sm:px-4"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="editor-save-btn"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[#0654BA] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#054a9e] disabled:opacity-60 sm:px-6"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {saving ? "Saving…" : "Save photo"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AspectIcon({ aspect, active }) {
  const color = active ? "#0654BA" : "#71717A";
  if (aspect.id === "free") {
    return <Search className="w-5 h-5" style={{ color }} />;
  }
  const r = aspect.value;
  const box = 22;
  let w, h;
  if (r >= 1) { w = box; h = box / r; } else { h = box; w = box * r; }
  return (
    <div
      style={{ width: w, height: h, borderColor: color }}
      className="border-2 rounded-sm"
    />
  );
}

function BigBtn({ testid, onClick, icon: Icon, label, sub, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 min-w-[120px] flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 transition ${
        active ? "border-[#0654BA] bg-[#E0EDFA]" : "border-zinc-200 hover:border-zinc-300 bg-white"
      }`}
    >
      <Icon className={`w-5 h-5 ${active ? "text-[#0654BA]" : "text-zinc-700"}`} />
      <div className="text-xs font-semibold leading-none">{label}</div>
      <div className="text-[10px] text-zinc-500 leading-none">{sub}</div>
    </button>
  );
}

function SliderRow({ testid, icon: Icon, label, value, onChange, onReset }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-zinc-700 flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5 text-zinc-500" /> {label}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500 tabular-nums w-10 text-right">{value}%</span>
          <button
            type="button"
            onClick={onReset}
            data-testid={`${testid}-reset`}
            className="text-xs text-zinc-400 hover:text-[#0654BA]"
          >
            reset
          </button>
        </div>
      </div>
      <Slider
        min={50}
        max={150}
        step={1}
        value={[value]}
        onValueChange={(v) => onChange(v[0])}
        data-testid={testid}
      />
    </div>
  );
}
